import { type ReactElement, useEffect } from 'react';

import Check from '@/components/Icons/Check';
import ProgressIndicator from '@/components/ProgressIndicator';
import Spacer from '@/components/Spacer';
import * as format from '@/format';
import useNoodleApi from '@/hooks/useNoodleApi';
import * as tsClient from '@/tsClient';
import { type NoodleProductPrice } from '@/typings/api-models';

import CreditFinancialReportCard from './CreditFinancialReportCard';
import s from './CreditReportModal.module.scss';

const DETAILS = [
  'Primary property value with comparables, property deeds and assessments, related addresses',
  'Registrations for all titled vehicles',
  'UCC filings and property deeds',
  'Experian, TransUnion, and Equifax creditor and public records data with bankruptcy-specific addresses',
  'LexisNexis 8-year bankruptcy, liens, and judgments search',
  'Current and predictive post-bankruptcy credit score for the primary and joint debtor',
];

type Price = {
  priceId: string;
  price: number;
  currency: string;
  numBookings: number;
  priceTitle: string;
};

type Props = {
  onSelectPrice: ({ priceId, price, currency, numBookings, priceTitle }: Price) => void;
  nextStep: () => void;
  creatorSlug?: string;
};

const CreditFinancialReport = ({ onSelectPrice, nextStep, creatorSlug }: Props): ReactElement => {
  const {
    data: productPrices,
    fetchingState,
    getData: getProductPrices,
  } = useNoodleApi(tsClient.creditReportsB2C.getNoodleProducts);

  useEffect(() => {
    if (creatorSlug) {
      getProductPrices({ creatorSlug });
    }
  }, [getProductPrices, creatorSlug]);

  const handleClick = (
    priceId: string,
    price: number,
    currency: string,
    numBookings: number,
    priceTitle: string,
  ): void => {
    onSelectPrice({
      currency,
      numBookings,
      price,
      priceId,
      priceTitle,
    });
    nextStep();
  };

  if (fetchingState.isFetching) {
    return <ProgressIndicator isCentered />;
  }

  return (
    <div>
      <div className={s.reportCards}>
        {productPrices
          ?.filter((price: NoodleProductPrice) => price.isActive)
          .map((price: NoodleProductPrice) => (
            <CreditFinancialReportCard
              key={price.id}
              title={price.priceTitle ?? ''}
              description={price.priceDescription?.html ?? ''}
              buttonLabel={`Buy for ${format.price.withCurrency(price.price, price.currency)}`}
              onClick={() =>
                handleClick(price.id, price.price, price.currency, price.numBookings ?? 1, price.priceTitle ?? '')
              }
            />
          ))}
      </div>
      <Spacer size={16} />

      <div className='body-sm-bold'>What’s included:</div>
      <Spacer size={8} />
      {DETAILS.map((item, itemKey) => (
        <div key={itemKey} className={s.reportDetails}>
          <Check size={12} color='var(--color-success)' />
          <div className='caption'>{item}</div>
        </div>
      ))}
    </div>
  );
};

export default CreditFinancialReport;
